import {Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, filter, Observable, shareReplay, Subject, takeUntil, tap} from "rxjs";
import {emptyEntityKey, EntityKey} from "@pma-entity/pma-entity-types";
import {Widget} from "@feature/dashboards";
import {ActivatedRoute, Router} from "@angular/router";
import {MojoRouterEventsService} from "@routing/mojo-router-events.service";
import {getActivatedRoute} from "@routing/route-util";

@Injectable({
  providedIn: 'root',
})
export class EntityDataLayoutService
  implements OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly viewNameSubject = new BehaviorSubject<string>(undefined);
  private readonly entityKeySubject = new BehaviorSubject<EntityKey>(undefined);
  private readonly listerTitleSubject = new BehaviorSubject<string>(undefined);
  private readonly dashboardTitleSubject = new BehaviorSubject<string>(undefined);
  private readonly refSubject = new BehaviorSubject<string>(undefined);
  private readonly maximisedWidgetSubject = new BehaviorSubject<Widget>(undefined);
  private readonly activatedRouteSubject = new BehaviorSubject<ActivatedRoute>(undefined);

  readonly ViewName$: Observable<string> = this.viewNameSubject.asObservable().pipe(
    shareReplay()
  );

  readonly EntityKey$: Observable<EntityKey> = this.entityKeySubject.asObservable().pipe(
      filter(x => !!x),
      shareReplay()
    );

  readonly ListerTitle$: Observable<string> = this.listerTitleSubject.asObservable().pipe(
      shareReplay()
    );

  readonly DashboardTitle$: Observable<string> = this.dashboardTitleSubject.asObservable().pipe(
      shareReplay()
    );

  readonly Ref$: Observable<string> = this.refSubject.asObservable().pipe(
      shareReplay()
    );

  readonly MaximisedWidget$: Observable<Widget> = this.maximisedWidgetSubject.asObservable().pipe(
      shareReplay()
    );

  readonly ActivatedRoute$: Observable<ActivatedRoute> = this.activatedRouteSubject.asObservable().pipe(
      filter(x => !!x),
      shareReplay()
    );

  constructor(
    routerEvents: MojoRouterEventsService,
    router: Router) {

    routerEvents.NavigationEnd$.pipe(
      takeUntil(this.destroy$),
      tap(_ => this.updateActivatedRoute(getActivatedRoute(router)))
    ).subscribe();

    routerEvents.NavigationStart$.pipe(
      takeUntil(this.destroy$),
      tap(_ => this.clear())
    ).subscribe();

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getViewName(): string {
    return this.viewNameSubject.value;
  }

  updateViewName(value: string) {
    this.viewNameSubject.next(value);
  }

  getEntityKey(): EntityKey {
    return this.entityKeySubject.value;
  }

  updateEntityKey(value: EntityKey) {
    this.entityKeySubject.next(value);
  }

  getListerTitle(): string {
    return this.listerTitleSubject.value;
  }

  updateListerTitle(value: string) {
    this.listerTitleSubject.next(value);
  }

  getDashboardTitle(): string {
    return this.dashboardTitleSubject.value;
  }

  updateDashboardTitle(value: string) {
    this.dashboardTitleSubject.next(value);
  }

  getRef(): string {
    return this.refSubject.value;
  }

  updateRef(value: string) {
    this.refSubject.next(value);
  }

  getMaximisedWidget(): Widget {
    return this.maximisedWidgetSubject.value;
  }

  updateMaximisedWidget(value: Widget) {
    this.maximisedWidgetSubject.next(value);
  }

  getActivatedRoute(): ActivatedRoute {
    return this.activatedRouteSubject.value;
  }

  updateActivatedRoute(value: ActivatedRoute) {
    this.activatedRouteSubject.next(value);
  }

  /**
   * Blanks the displayed title values.
   *
   * @remarks
   * Calling this from the NavigationStart event minimizes the duration
   * of display of incorrect title for 'intermediate' child components
   * initialized during routing traversal.
   */
  clear(): void {
    this.updateEntityKey(emptyEntityKey);
    this.updateViewName('');
    this.updateListerTitle('');
    this.updateDashboardTitle('');
    this.updateRef('');
  }
}
